import { FC, useEffect, useState } from 'react';
import { Modal, Form } from 'antd';
import { useDispatch, useSelector } from "react-redux";

// Shared UI Library Components
import {
  CustomButton,
  DropdownElement,
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './timezones-initialization-modal.scss';

// Store entities
import { getDateState } from "../../store/date/date.selectors";
import { getUserState } from "../../store/user/user.selectors";
import { setSelectedTimeZone, saveSelectedTimeZone } from "../../store/date/date.thunks";
import { DateSliceActions } from '../../store/date/date.slice';
import {getBrowserTimezone} from "../../utils/getBrowserTimezone";
import {openNotification} from "../../store/app-notifications/app-notifications.thunks";

type TFormValue = {
  timeZone: string,
};

type TTimezonesInitializationModal = {}

export const TimezonesInitializationModal: FC<TTimezonesInitializationModal> = (
  {}: TTimezonesInitializationModal
) => {

  const [form] = Form.useForm<TFormValue>();

  const { timezonesOptions, localTimezone, loading, showChangeTimeZoneModal } = useSelector(getDateState);
  const { user } = useSelector(getUserState);

  const dispatch = useDispatch<any>();
  const storageTz = localStorage.getItem('timezone');
  const storageTzOffset = localStorage.getItem('timezoneOffset');
  const browserTimeZone = getBrowserTimezone()
  const browserTimeZoneOffset = -new Date().getTimezoneOffset();

  const getStringOffest = () => {
    const offsetString = new Date().toTimeString().match(/GMT([+-]\d{2})(\d{2})/);
    if (offsetString) {
      const [ , hours, minutes ] = offsetString;
      return `(${hours}:${minutes})`;
    }
  }

  const saveTimeZoneHandler = async (): Promise<void> => {
    const formValue = form.getFieldsValue();
    const formTimezoneOffset = timezonesOptions.find(tz => tz.value === formValue.timeZone)?.offset ?? 0;
    if (formTimezoneOffset === browserTimeZoneOffset) {
      dispatch(setSelectedTimeZone({ timeZone: formValue.timeZone }));
      dispatch(saveSelectedTimeZone({ timeZone: formValue.timeZone }));
      localStorage.setItem('timezone', formValue.timeZone);
      localStorage.setItem('timezoneOffset', formTimezoneOffset.toString() ?? '0');
      dispatch(DateSliceActions.setShowChangeTimeZoneModal(false))
      dispatch(openNotification({
        type: 'success',
        description: 'Time Zone Changed Successfully!'
      }));
    } else {
      dispatch(openNotification({
        type: 'warning',
        description: `Please select your time zone from the list. Your time zone is not correct. Your time zone is: ${browserTimeZone} ${getStringOffest()}`,
      }));
    }
  }

  useEffect(() => {
    if (!user) return

    if (storageTzOffset !== browserTimeZoneOffset.toString()) {
      dispatch(DateSliceActions.setShowChangeTimeZoneModal(true))
    }

  }, [ user, browserTimeZoneOffset, storageTzOffset]);

  return (
    <Modal
      rootClassName="timezones-initialization-modal"
      width={ '394px' }
      open={ showChangeTimeZoneModal }
      closeIcon={ null }
      destroyOnClose={ true }
      closable={ false }
      centered={ true }
      title={ 'Save Your Time Zone' }
      footer={ [
        <CustomButton key="save"
                      text={ 'Save' }
                      loading={ loading }
                      onClick={ saveTimeZoneHandler }
        />
      ] }
    >
      <Form form={ form } preserve={ false } className="timezones-initialization-modal--body modal-body">
        <div className="modal-body--item item-subtitle">
          We've detected your time zone. If it's incorrect, please change it now. This is necessary for the availability
          schedule. <br/>Your time zone is: {browserTimeZone} {getStringOffest()}
        </div>
        { localTimezone && <div className="modal-body--item">
          <Form.Item name={ 'timeZone' } initialValue={ localTimezone }>
            <DropdownElement withoutAll={ true } placeholder={ 'Select your time zone' } options={ timezonesOptions }/>
          </Form.Item>
        </div> }
      </Form>
    </Modal>
  )
};
